"use client";

// AFTER A RELEASE, A TAB'S NEXT IN-APP NAVIGATION IS A FULL PAGE LOAD.
//
// After a deploy every tab opened before it goes on running the OLD client,
// which is both waste (v0.49.0's tabs kept asking for full dumps) and a trap:
// Next recomputes server action ids per build, so an old tab's next action is
// unknown to the new server.
//
// THE POLL SAYS. Every GET /api/store answer, the 304 included, carries the
// server's build in an `x-ironside-build` header (app/api/store/route.ts), and
// StoreSync hands it here. A build that is not the one this document was drawn
// by (its meta tag, pageBuildId()) marks the tab stale.
//
// A STALE TAB IS NEVER RELOADED UNDER ITS READER. The first answer reloaded in
// place once the tab was hidden or idle for a minute, and held off while a
// field held typed text; now there is no reload in place and no popup. Instead
// the tab's next navigation to another page is a document load, which is a
// break point the reader chose:
//
//   - a click on an in-app link (watchStaleLinks: one capture-phase listener
//     on the window, ahead of next/link's own handler, which then sees the
//     click prevented and stands aside);
//   - the app's own router.push and router.replace (useAppRouter,
//     lib/app-router.ts, the only door to Next's router; the lint refuses
//     next/navigation's useRouter anywhere else).
//
// Next does half of this already: a navigation whose fetch is answered by a
// different build becomes a document load (fetch-server-response.js, the
// build id check). What it misses is a navigation answered from the router's
// cache, a prefetched or recently visited page, which renders on the old
// client. This catches both before the router is asked.
//
// WHAT IS NOT A BREAK POINT, AND STAYS A CLIENT NAVIGATION:
//   - the same page with another query or hash: a filter, a tab, a popup's
//     `?open=`. Its reader is still on the page;
//   - a link out of the app, or to /api or /_next (a download);
//   - a tab with a write in the air or a refused one in the failure notice
//     (store-persist's writesSettled()): the notice's value and retry live
//     only in this document. The next navigation asks again;
//   - a tab where something that outlives a navigation holds text a document
//     load would lose (holdFullLoad: the Feedback panel, open with words in
//     it, sits in the root layout and survives a client navigation);
//   - a tab that already loaded a document to this build and still disagrees
//     (takeReloadGuard, shared with the error boundary's reloadIfStale).
//
// The editor's own unsent typing is not on that list: its pagehide flush sends
// it with keepalive and marks the save, and the page that comes back takes it
// silently (lib/editor-own-save.ts). Any other field on
// the page being left is unmounted by a client navigation too, so a document
// load loses nothing there that the click was not already leaving.
//
// ⚠️ IT DOES NOTHING ON "development". `next dev` has no BUILD_ID, recompiles
// in place, and reloads its own tabs. The test-only app/api/test/build-id makes
// the server report another build on the e2e stack.
//
// ⚠️ IT FIXES THE NEXT DEPLOY ONWARD. A tab running a client from before this
// file cannot be taught anything; the old one reloads itself as it always did.

import { pageBuildId, takeReloadGuard } from "./stale-build";
import { writesSettled } from "./store-persist";

export interface StaleTabDeps {
  /** The build this document was drawn by. */
  pageBuild?: () => string | null;
  /** True when no write is in the air or refused. */
  writesSettled?: () => boolean;
  storage?: Pick<Storage, "getItem" | "setItem">;
  location?: Pick<Location, "href" | "assign" | "replace">;
}

export interface StaleTab {
  /** What the server said its build is, from a poll's header; null when it said nothing. */
  noteServerBuild(build: string | null): void;
  /** The server's build while it differs from this page's, else null. */
  staleFor(): string | null;
  /**
   * Navigate to `href` with a document load instead, if this tab is stale and
   * the move is a break point (above). True when it did; the caller then does
   * nothing more.
   */
  loadInstead(href: string, how?: "push" | "replace"): boolean;
}

// What, outside the page being left, would a document load lose? Each holder
// answers true while it holds something only this document has.
const holders = new Set<() => boolean>();

/** Hold a stale tab's document-load navigation while `holds()` is true. Returns the release, for an effect's cleanup. */
export function holdFullLoad(holds: () => boolean): () => void {
  holders.add(holds);
  return () => {
    holders.delete(holds);
  };
}

function held(): boolean {
  for (const holds of holders) if (holds()) return true;
  return false;
}

/** Same origin, another page, and a page (not /api, not a static file). */
function breakPoint(href: string, here: string): URL | null {
  let to: URL;
  let from: URL;
  try {
    from = new URL(here);
    to = new URL(href, from);
  } catch {
    return null;
  }
  if (to.origin !== from.origin) return null;
  if (to.pathname === from.pathname) return null; // a query or a hash on the same page
  if (/^\/(api|_next)(\/|$)/.test(to.pathname)) return null;
  return to;
}

export function createStaleTab(deps: StaleTabDeps = {}): StaleTab {
  const pageBuild = deps.pageBuild ?? pageBuildId;
  const settled = deps.writesSettled ?? writesSettled;
  const loc = () => deps.location ?? (typeof location === "undefined" ? undefined : location);

  let target: string | null = null; // the server's build, while it differs from the page's

  return {
    noteServerBuild(build) {
      const mine = pageBuild();
      // Nothing to compare: no header (an older server), no meta tag, or dev.
      if (!build || !mine || mine === "development") return;
      // The same build again (a quick rollback before any navigation) is fresh.
      target = build === mine ? null : build;
    },
    staleFor: () => target,
    loadInstead(href, how = "push") {
      if (target === null) return false;
      const l = loc();
      if (!l) return false;
      const to = breakPoint(href, l.href);
      if (!to) return false;
      if (!settled() || held()) return false;
      if (!takeReloadGuard(target, deps.storage)) return false;
      if (how === "replace") l.replace(to.href);
      else l.assign(to.href);
      return true;
    },
  };
}

// The one the app uses: StoreSync feeds it every poll's header.
let shared: StaleTab | null = null;
export function staleTab(): StaleTab {
  shared ??= createStaleTab();
  return shared;
}

/**
 * A click that next/link (or the browser) would follow, or null: the primary
 * button, no modifier (a new tab or window is not this tab's navigation), an
 * anchor with an href, no `download`, and no target other than this frame.
 */
export function followedHref(e: MouseEvent): string | null {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const start = e.target instanceof Element ? e.target : null;
  const a = start?.closest("a[href]");
  if (!a || a.hasAttribute("download")) return null;
  const target = a.getAttribute("target");
  if (target && target !== "_self") return null;
  return a.getAttribute("href");
}

/**
 * Turn a stale tab's in-app link clicks into document loads. Capture phase on
 * the window, so it runs before React's listener: preventing the click there
 * is what next/link checks (`e.defaultPrevented`) before it navigates, and
 * the link's own onClick handlers still run. Returns the stop.
 */
export function watchStaleLinks(tab: StaleTab = staleTab()): () => void {
  const onClick = (e: MouseEvent) => {
    const href = followedHref(e);
    if (href === null) return;
    if (tab.loadInstead(href)) e.preventDefault();
  };
  window.addEventListener("click", onClick, true);
  return () => window.removeEventListener("click", onClick, true);
}
